import { NetworkType, type RecipientItem } from '@capsicum/common';
import {
	AddRecipientCard,
	Button,
	ButtonSection,
	RecipientCard,
	RecipientCardSkeleton,
	TextButton,
	useRouter
} from '@capsicum/common-web';
import { ncwSendAssetAtom, useNCWRecipientsList } from '@capsicum/features/nc-wallet/shared';
import { useWalletWithdrawRecipients } from '@capsicum/features/wallet/shared';
import { useAtom } from 'jotai';
import { useState } from 'react';
import styled from 'styled-components';

export const NCWRecipientsList = () => {
	const [selectedRecipient, setSelectedRecipient] = useState<RecipientItem | undefined>(undefined);

	const { push } = useRouter();
	const { data: recipients, isLoading } = useWalletWithdrawRecipients(NetworkType.CC2);
	const [sendAsset] = useAtom(ncwSendAssetAtom);

	const { intlText } = useNCWRecipientsList();

	return (
		<S.Wrapper>
			<S.Container>
				<S.Title>{intlText.title}</S.Title>
				<S.TextContainer $isLoading={isLoading}>
					<S.Text>{intlText.addRecipientText}</S.Text>
					<S.AddRecipientButton
						testID="NCWallet-recipientsList-addRecipient-textButton"
						text={intlText.addRecipientLink}
						onClick={() => push(`recipients/create`)}
					/>
				</S.TextContainer>

				<S.ListContainer>
					{isLoading ? (
						<RecipientCardSkeleton />
					) : (
						recipients?.map(recipient => {
							return (
								<RecipientCard
									key={recipient.id}
									recipient={recipient}
									selectedRecipient={selectedRecipient}
									onEditRecipient={() => {
										push(`recipients/edit/${recipient.id}`);
									}}
									onSelectRecipient={setSelectedRecipient}
								/>
							);
						})
					)}
				</S.ListContainer>
				<AddRecipientCard network={NetworkType.CC2} onCardClick={() => push(`recipients/create?`)} />
			</S.Container>
			<S.ButtonWrapper>
				<Button
					disabled={!selectedRecipient}
					isLoading={isLoading}
					testID="NCWallet-recipientsList-selectRecipient-submitButton"
					text={intlText.buttonText}
					onClick={() =>
						sendAsset?.assetType &&
						push(
							{
								chiliz: `/nc-wallet/send/chiliz/confirm?recipientId=${selectedRecipient?.id}`,
								token: `/nc-wallet/send/fan-tokens/confirm?recipientId=${selectedRecipient?.id}`
							}[sendAsset?.assetType]
						)
					}
				/>
			</S.ButtonWrapper>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
	`;
	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.large} ${({ theme }) => theme.spacings.small};
		width: 100%;
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => theme.spacings.small};
			margin-bottom: ${({ theme }) => theme.spacings.xxxlarge};
			overflow: hidden;
		}
	`;

	export const Title = styled.p`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title5};
		margin: 0 0 ${({ theme }) => theme.spacings.medium};
	`;
	export const TextContainer = styled.div<{ $isLoading?: boolean }>`
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		margin: 0 0 ${({ theme }) => theme.spacings.small};
		align-items: center;
		visibility: ${({ $isLoading }) => ($isLoading ? 'hidden' : 'visible')};
	`;

	export const Text = styled.p`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text3};
		margin: 0;
	`;

	export const AddRecipientButton = styled(TextButton)`
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			height: 40px;
		}
	`;

	export const ButtonWrapper = styled(ButtonSection)`
		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			margin-top: 0;
			padding-bottom: ${({ theme }) => theme.spacings.xxlarge};
		}
	`;

	export const ListContainer = styled.div`
		display: flex;
		flex-direction: column;
		padding-bottom: ${({ theme }) => theme.spacings.large};
		width: 100%;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;
	export const TooltipTitle = styled.span`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title6};
		margin: 0 0 ${({ theme }) => theme.spacings.xxsmall};
		display: block;
	`;
	export const TooltipText = styled.span`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text3};
		display: block;
		margin: 0;
	`;
}
